import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, PartyPopper, Crown, Gift, ArrowRight } from "lucide-react";
import { API_BASE } from "@/App";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("checking"); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const { data } = await managedAxios.get(
        `${API_BASE}/payments/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(data);

        // Refresh user data in localStorage
        try {
          const userResponse = await managedAxios.get(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem("user", JSON.stringify(userResponse.data));
        } catch (e) {
          console.error("Failed to refresh user data:", e);
        }
      } else if (data.status === "expired" || data.payment_status === "failed") {
        setStatus("error");
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error("Payment status check failed:", err);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-12 pb-8 text-center">
            <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Verifying Payment...
            </h2>
            <p className="text-slate-500">
              Please wait while we confirm your payment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Verification Failed
            </h2>
            <p className="text-slate-500 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/upgrade")}
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-amber-200 shadow-xl">
        <CardContent className="pt-12 pb-8 text-center">
          {/* Confetti Animation */}
          <div className="relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2">
              <PartyPopper className="w-8 h-8 text-amber-500 animate-bounce" />
            </div>
            <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Crown className="w-12 h-12 text-white" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            Welcome to PRO! 🎉
          </h2>
          <p className="text-slate-600 mb-6">
            Your payment was successful. You now have full access to all CLARIAS PRO features.
          </p>

          {/* Payment Details */}
          {paymentData && (
            <div className="bg-white rounded-xl p-4 mb-6 text-left border border-amber-100">
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Plan</span>
                <span className="font-semibold text-slate-900">{paymentData.plan_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-semibold text-slate-900">
                  {paymentData.currency === "inr" ? "₹" : "$"}{paymentData.amount}
                </span>
              </div>
            </div>
          )}

          {/* Features Unlocked */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-4 mb-6 text-left">
            <p className="text-amber-400 font-semibold text-sm mb-3">✨ FEATURES UNLOCKED</p>
            <ul className="space-y-2">
              {[
                "Unlimited AI Analysis",
                "Full Intelligence Reports",
                "Advanced Clarity AI",
                "Premium Materials"
              ].map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Refer & Earn nudge — high-intent post-purchase moment */}
          <div
            className="bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded-xl p-4 mb-4 text-left cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate("/refer")}
            data-testid="success-refer-cta"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">Earn free PRO months 🎁</p>
                <p className="text-xs text-slate-600">Invite friends — each PRO signup gives you 100 credits. 300 = 1 free month.</p>
              </div>
              <ArrowRight className="w-4 h-4 text-amber-600 shrink-0" />
            </div>
          </div>

          <Button
            onClick={() => navigate("/dashboard")}
            className="w-full bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600 text-white py-6 text-lg font-semibold"
          >
            Start Exploring PRO Features
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
